<script setup lang="ts">
import { useApiFetch } from "~/composible/useApiFetch";
import {defineBreadcrumb} from "#imports";
import ReviewPreview from "~/components/ReviewPreview.vue";
import useBook from "~/composible/useBook";
import ContentBlock from "~/components/ContentBlock.vue";

const router = useRouter();
const route = useRoute();
const { getLinkToApp } = useBook();

const { data, error, status} = await useApiFetch(`/reviews/${route.params.slug}`)

if (error.value?.statusCode === 404) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

const { data: recent } = await useApiFetch(`/reviews/recent?exclude[]=${data.value.review.id}`, {
  transform: (recent) => recent.data
});

const crumbs = [
  {link: '/book-reviews', label: 'Novel Reviews'},
  {label: data.value.review.title }
];

const { data: popularBooks, pending: pendingPopular } = useApiFetch(`/novels/_/similar`, {
  transform: (popularBooks) => popularBooks.data,
  lazy: true,
});

useSeoMeta({
  titleTemplate: '%s – Read on FictionMe',
  title: `Book Review of ${data.value.review.book.title} by ${data.value.review.book.author}`,
  description: `Dive into review of “${data.value.review.book.title}” by ${data.value.review.book.author}. Find out if this novel meets your reading preferences with our detailed evaluation and insights`,
  ogTitle: `Book Review of ${data.value.review.book.title} by ${data.value.review.book.author}`,
  ogDescription: `Dive into review of “${data.value.review.book.title}” by ${data.value.review.book.author}. Find out if this novel meets your reading preferences with our detailed evaluation and insights`,
  twitterTitle: `Book Review of ${data.value.review.book.title} by ${data.value.review.book.author}`,
  twitterDescription: `Dive into review of “${data.value.review.book.title}” by ${data.value.review.book.author}. Find out if this novel meets your reading preferences with our detailed evaluation and insights`,
});

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      defineListItem({
        position: 1,
        name: 'Novel Reviews',
        item: '/book-reviews'
      }),
      defineListItem({
        position: 2,
        name: data.value.review.title,
      }),
    ]
  }),
  defineReview({
    name: data.value.review.title,
    description: data.value.review.description,
    image: data.value.review.image_url,
    author: defineOrganization({
      name: 'FictionMe'
    }),
    itemReviewed: defineBook({
      name: data.value.review?.book.title,
      description: data.value.review?.book.description
    })
  })
]);
</script>

<template>
    <div class="container py-8">
      <div class="page-content flex flex-col gap-8">
        <breadcrumbs :breadcrumbs="crumbs" />

        <div class="grid grid-cols-[1fr_272px] gap-7 max-lg:grid-cols-1">
          <div class="flex flex-col gap-4 max-md:gap-2">
            <lazy-f-card>
              <div class="flex flex-col gap-8 max-md:gap-4">
                <h1 class="text-[40px] leading-[42px] font-semibold max-md:text-2xl max-md:leading-6">{{ data.review.title }}</h1>

                <ul v-if="data.review.tags && data.review.tags.length > 0" class="flex flex-wrap gap-2 max-h-[70px] overflow-hidden">
                  <li v-for="tag in data.review.tags">
                    <nuxt-link :to="`/stories/${tag.slug}-novels`">
                      <tag>{{ tag.name}}</tag>
                    </nuxt-link>
                  </li>
                </ul>

                <div class="font-light text-lg">By <span class="text-primary font-semibold">FictionMe</span></div>

                <content-block :html="data.review.content" :book="data.review.book" />

                <div class="flex justify-center" v-if="data.review.book">
                  <div class="flex gap-6 w-full max-w-lg max-md:flex-col max-md:gap-4">
                    <nuxt-link
                        :to="`/books/${data.review.book.slug }`"
                        class="flex-1 py-3 max-md:py-2 px-5 max-md:px-4 border-[1px] text-xl border-primary text-center rounded-lg text-violet-800 dark:text-violet-500 dark:hover:text-violet-600"
                    >
                      Book page
                    </nuxt-link>
                    <a
                        :href="getLinkToApp(data.review.book.id)"
                        class="flex-1 items-center py-3 max-md:py-2 px-5 max-md:px-4 text-xl bg-fm-gradient rounded-lg !text-white text-center hover:bg-fm-gradient-active"
                    >
                      Read book in the App
                    </a>
                  </div>
                </div>
              </div>
            </lazy-f-card>

            <div class="flex justify-between">
              <nuxt-link v-if="data.meta.prev" :to="`/book-reviews/${data.meta.prev}`" class="flex items-center font-semibold text-violet-800 dark:text-violet-500 dark:hover:text-violet-600">
                <icon name="fm:arrow-right" class="mr-1 text-xl rotate-180" />
                Previous Review
              </nuxt-link>
              <span v-else></span>
              <nuxt-link v-if="data.meta.next" :to="`/book-reviews/${data.meta.next}`" class="flex items-center font-semibold text-violet-800 dark:text-violet-500 dark:hover:text-violet-600">
                Next Review
                <icon name="fm:arrow-right" class="ml-1 text-xl" />
              </nuxt-link>
              <span v-else></span>
            </div>
          </div>
          <div class="flex flex-col">
            <div class="grid grid-cols-1 max-lg:grid-cols-2 gap-4">
              <review-preview
                  v-for="review in recent"
                  :review="review"
                  :key="review.id"
              />
            </div>
          </div>
        </div>

        <lazy-f-card
            v-if="popularBooks && popularBooks.length > 0"
            title="Popular Novels You May Like"
        >
          <div class="flex gap-5 max-md:gap-3 overflow-auto scrollbar-hidden">
            <book-card
                v-for="book in popularBooks"
                :book="book"
                :key="book.id"
            />
          </div>
        </lazy-f-card>

        <cta-banner />
      </div>
    </div>
</template>

<style scoped>

</style>